import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import bcrypt from 'bcryptjs';

import { useAuth } from '../../contexts/AuthContext';
import { db, supabase } from '../../services/supabase';
import { AVATARS } from '../../utils/constants';

import AppLayout from '../../components/Layout/AppLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import InfoTooltip from '../../components/UI/InfoTooltip';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { pushRpc } from '../../services/pushNotifications';
import { copyTextToClipboard } from '../../utils/clipboard';
import '../../styles/brand-typography.css';
import './AddChild.css';
import { AGE_GROUP_OPTIONS } from '../../utils/ageGroups';
import { useFamilyStatus } from '../../contexts/FamilyStatusContext';

const SUFFIX_OPTIONS = ['', 'Jr.', 'Sr.', 'II', 'III', 'IV', 'V'];

const ChildAvatar = ({ emoji, url }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  let imageUrl = '';
  try { const parsed = new URL(url); if (['https:', 'http:'].includes(parsed.protocol)) imageUrl = parsed.href; } catch { /* Fall back to the selected emoji. */ }
  return imageUrl && !failed ? <img src={imageUrl} alt="Child avatar" onError={() => setFailed(true)} className="mx-auto h-12 w-12 shrink-0 rounded-full object-cover" /> : <span className="text-3xl" aria-label="Selected emoji avatar">{emoji || '🧒'}</span>;
};

const AddChild = () => {
  const navigate = useNavigate();
  const { parent } = useAuth();
  const {
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useFamilyStatus();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;

  const [loading, setLoading] = useState(false);
  const [createdChild, setCreatedChild] = useState(null);
  const [enableChildPush, setEnableChildPush] = useState(false);
  const [pushChoiceStatus, setPushChoiceStatus] = useState('');
  const [savingPushChoice, setSavingPushChoice] = useState(false);
  const canManageChildPush = parent?.role === 'owner';
  const saveChildPushChoice = async childId => {
    await pushRpc(supabase, 'push_child_preferences', { p_child_id: childId, p_consent: true, p_daily: true, p_weekly: true });
  };

  const [formData, setFormData] = useState({
    first_name: '',
    middle_initial: '',
    last_name: '',
    suffix: '',
    nickname: '',
    age_group: '',
    avatar: AVATARS[0],
    avatar_url: '',
    pin: '',
    confirm_pin: '',

    // Default ON because MyPiggy is designed
    // primarily for younger kids and family involvement.
    require_chore_approval: true,
  });

  const [error, setError] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const submitting = useRef(false);
  const pageHeading = useRef(null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    pageHeading.current?.focus({ preventScroll: true });
  }, [createdChild]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError('This family subscription is currently read-only.');
      return;
    }

    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError('');

    try {
      // --------------------------------
      // Structured-name validation
      // --------------------------------

      if (!formData.first_name.trim()) {
        throw new Error('First name is required');
      }

      if (!formData.last_name.trim()) {
        throw new Error('Last name is required');
      }

      if (
        formData.middle_initial.trim() &&
        !/^[A-Za-z]$/.test(formData.middle_initial.trim())
      ) {
        throw new Error('Middle initial must be a single letter');
      }

      if (!formData.nickname.trim()) {
        throw new Error('Nickname / display name is required');
      }

      // Require an age group instead of collecting an exact age.
      if (
        !AGE_GROUP_OPTIONS.some(
          (option) => option.value === formData.age_group
        )
      ) {
        throw new Error('Please select an age group');
      }

      // Validate the child password.
      if (
        formData.pin.length < 6 ||
        formData.pin.length > 64
      ) {
        throw new Error('Password must be 6 to 64 characters');
      }

      if (formData.pin !== formData.confirm_pin) {
        throw new Error('Passwords do not match');
      }

      if (formData.avatar_url.trim()) {
        try {
          const picture = new URL(formData.avatar_url.trim());
          if (!['https:', 'http:'].includes(picture.protocol)) {
            throw new Error('Invalid protocol');
          }
        } catch {
          throw new Error('Picture URL must be a valid http:// or https:// link.');
        }
      }

      // --------------------------------
      // Hash PIN
      // --------------------------------

      const pinHash = await bcrypt.hash(formData.pin, 10);

      // --------------------------------
      // Create child
      //
      // IMPORTANT:
      // We intentionally do NOT send user_id or full_name.
      //
      // PostgreSQL now:
      // - generates the permanent MyPiggy Login ID
      // - builds full_name from the structured name fields
      // --------------------------------

      const child = await db.createChild({
        family_id: parent.family_id,

        first_name: formData.first_name.trim(),

        middle_initial:
          formData.middle_initial.trim().toUpperCase() || null,

        last_name: formData.last_name.trim(),

        suffix: formData.suffix || null,

        nickname: formData.nickname.trim(),

        age_group: formData.age_group,

        avatar: formData.avatar,

        avatar_url:
          formData.avatar_url.trim() || null,

        pin_hash: pinHash,

        // This ONE setting controls chore approval
        // for ALL chores completed by this child:
        //
        // - Starter chores
        // - Parent-created chores
        // - Kid-created personal chores
        require_chore_approval:
          formData.require_chore_approval,

        status: 'active',
      });

      // Stay on this screen long enough to show the parent
      // the database-generated MyPiggy Login ID.
      // Child creation is already complete. A preferences failure must never invite duplicate creation.
      if (enableChildPush && canManageChildPush) {
        try { await saveChildPushChoice(child.id); setPushChoiceStatus('saved'); }
        catch { setPushChoiceStatus('failed'); }
      } else { setPushChoiceStatus('off'); }
      setCreatedChild(child);
      setFormData((previous) => ({ ...previous, pin: '', confirm_pin: '' }));
    } catch (error) {
      console.error('AddChild: error', error);

      setError(
        error?.message ||
          'Unable to add child. Please try again.'
      );
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((previous) => ({
      ...previous,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddAnother = () => {
    setCreatedChild(null);
    setEnableChildPush(false);
    setPushChoiceStatus('');
    setError('');
    setCopyStatus('');
    setShowAvatarPicker(false);

    setFormData({
      first_name: '',
      middle_initial: '',
      last_name: '',
      suffix: '',
      nickname: '',
      age_group: '',
      avatar: AVATARS[0],
      avatar_url: '',
      pin: '',
      confirm_pin: '',
      require_chore_approval: true,
    });
  };

  const handleCopy = async () => {
    try { setCopyStatus(await copyTextToClipboard(createdChild.user_id) ? 'Copied!' : 'Copy failed. Please copy the ID manually.'); }
    catch { setCopyStatus('Copy failed. Please copy the ID manually.'); }
  };

  if (createdChild) {
    return (
      <AppLayout title="Add Child" className="mypiggy-brand add-child" showBack onBack={() => navigate('/parent')}>
        <div className="mx-auto max-w-xl space-y-4">
          <Card className="p-4 sm:p-5">
            <div className="text-center"><ChildAvatar emoji={createdChild.avatar} url={createdChild.avatar_url} /><h2 ref={pageHeading} tabIndex={-1} className="mt-2 text-xl font-extrabold">Child Added Successfully!</h2><p className="mt-1 text-sm text-gray-600 dark:text-slate-300">{createdChild.nickname} is ready to use MyPiggy.</p></div>
            <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/35">
              <p className="text-center text-sm font-bold text-teal-900 dark:text-teal-200">{createdChild.first_name || createdChild.nickname}&apos;s MyPiggy Login ID</p>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2 rounded-lg border border-teal-200 bg-white p-3 dark:border-teal-800 dark:bg-slate-900"><span className="min-w-0 break-all text-center font-mono text-xl font-extrabold tracking-wide">{createdChild.user_id || 'Not assigned'}</span><Button type="button" variant="secondary" size="sm" disabled={!createdChild.user_id} onClick={handleCopy}>Copy Login ID</Button></div>
              <p role="status" className="mt-1 text-center text-xs font-semibold text-teal-900 dark:text-teal-200">{copyStatus}</p>
              <p className="mt-2 text-center text-sm text-teal-900 dark:text-teal-200">Use this ID with the password you just created.</p>
            </div>
            <p className="mt-2 text-center text-xs text-gray-600 dark:text-slate-300">You can find this ID again on the dashboard under Manage Child.</p>
          </Card>
          <Card className="p-4 text-sm">
            {pushChoiceStatus === 'saved' && <p>Daily Tips and Weekly Reminders are enabled for this child. Allow notifications in the Kids app on their device to receive them. Weekly reminders are sent only after seven days without activity.</p>}
            {pushChoiceStatus === 'off' && <p>Push notifications are off for this child. The Family Organizer can enable them later in Manage Child.</p>}
            {pushChoiceStatus === 'failed' && <div role="alert"><p>Your child was added, but we couldn’t confirm the notification choice was saved. Retry here or manage it in Manage Child.</p><Button type="button" size="sm" disabled={savingPushChoice} onClick={async () => {
              setSavingPushChoice(true); try { await saveChildPushChoice(createdChild.id); setPushChoiceStatus('saved'); } catch { setPushChoiceStatus('failed'); } finally { setSavingPushChoice(false); }
            }}>{savingPushChoice ? 'Saving…' : 'Retry notification choice'}</Button></div>}
          </Card>
          <AppDownloadCard audience="kids" />
          <div className="grid gap-2 sm:grid-cols-2"><Button type="button" onClick={() => navigate('/parent')}>Go to Parent Dashboard</Button><Button type="button" variant="secondary" onClick={handleAddAnother}>Add Another Child</Button></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Add Child" className="mypiggy-brand add-child" showBack onBack={() => navigate('/parent')}>
      <div className="mx-auto max-w-2xl">
        <Card className="p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2"><MyPiggyIcon name="kid-profile" size="sm" /><div><h2 ref={pageHeading} tabIndex={-1} className="text-xl font-extrabold">Add New Child</h2><p className="text-xs text-gray-600 dark:text-slate-300">MyPiggy creates your child’s Login ID automatically.</p></div></div>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isWriteLocked || loading} className={`space-y-4 ${isWriteLocked ? 'opacity-60' : ''}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="e.g. Jia" autoComplete="off" required />
                <Input label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="e.g. Parikh" autoComplete="off" required />
              </div>
              <details className="rounded-lg border border-gray-200 px-3 py-2 dark:border-slate-700"><summary className="cursor-pointer text-xs font-bold text-teal-800 dark:text-teal-200">Additional name details (optional){formData.middle_initial || formData.suffix ? ' • Added' : ''}</summary><div className="mt-3 grid grid-cols-2 gap-3"><Input label="Middle Initial" name="middle_initial" value={formData.middle_initial} onChange={(e) => setFormData((previous) => ({ ...previous, middle_initial: e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 1).toUpperCase() }))} maxLength={1} autoComplete="off" placeholder="M" /><div><label htmlFor="suffix">Suffix</label><select id="suffix" name="suffix" value={formData.suffix} onChange={handleChange}>{SUFFIX_OPTIONS.map((suffix) => <option key={suffix || 'none'} value={suffix}>{suffix || 'None'}</option>)}</select></div></div></details>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Nickname / Display Name" name="nickname" value={formData.nickname} onChange={handleChange} placeholder="What should MyPiggy call them?" helperText="Shown on the kid dashboard; their Login ID stays the same." required />
                <div><div className="mb-1.5 flex items-center gap-2"><label htmlFor="age_group" style={{ marginBottom: 0 }}>Age Group</label><InfoTooltip compact label="About child age groups">We store a broad age group rather than an exact age. Choose the best fit; there is no upper age limit for 18+.</InfoTooltip></div><select id="age_group" name="age_group" value={formData.age_group} onChange={handleChange} required><option value="">Select an age group</option>{AGE_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
              </div>
              <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/30"><div className="flex items-start gap-2"><input id="require-chore-approval" type="checkbox" checked={formData.require_chore_approval} onChange={(e) => setFormData((previous) => ({ ...previous, require_chore_approval: e.target.checked }))} className="mt-1 h-4 w-4 shrink-0 accent-teal-700" /><div className="min-w-0 flex-1"><label htmlFor="require-chore-approval">Require Parent Approval for Chores</label><p className="text-xs text-gray-600 dark:text-slate-300">{formData.require_chore_approval ? 'Review completed chores before rewards are added.' : 'Completed chores are approved and rewarded automatically.'}</p></div><InfoTooltip compact label="About chore approval">This setting applies to all chores completed by this child. When enabled, rewards wait for parent approval. When disabled, completed chores are approved automatically. You can change this later under Manage Child.</InfoTooltip></div></div>
              {canManageChildPush && <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/30">
                <label className="child-push-choice flex items-start gap-3"><input type="checkbox" checked={enableChildPush} onChange={event => setEnableChildPush(event.target.checked)} aria-describedby="child-push-explanation" className="mt-1 h-4 w-4 shrink-0 accent-teal-700" /><span className="min-w-0 flex-1">Enable Daily Tips and Weekly Reminders for my child</span></label>
                <p id="child-push-explanation" className="mt-2 text-xs text-gray-600 dark:text-slate-300">I allow MyPiggy to send notifications to my child’s device. They’ll receive daily money tips and a friendly reminder after seven days without activity. Notifications must also be allowed on the device. No balances, login IDs, or transaction details appear in notifications. You can change this setting anytime.</p>
              </div>}
              <div className="rounded-xl border border-gray-200 p-3 dark:border-slate-700">
                <div className="flex items-center gap-3"><ChildAvatar emoji={formData.avatar} url={formData.avatar_url} /><div className="flex-1"><p className="text-sm font-bold">Avatar</p><p className="text-xs text-gray-600 dark:text-slate-300">Choose a look for their profile.</p></div><Button type="button" size="sm" variant="secondary" aria-expanded={showAvatarPicker} aria-controls="child-avatar-picker" onClick={() => setShowAvatarPicker((value) => !value)}>{showAvatarPicker ? 'Done' : 'Change Avatar'}</Button></div>
                {showAvatarPicker && <div id="child-avatar-picker" className="mt-3 grid grid-cols-5 gap-2">{AVATARS.map((avatar) => <button key={avatar} type="button" aria-label={`Choose ${avatar} avatar`} aria-pressed={formData.avatar === avatar} onClick={() => setFormData((previous) => ({ ...previous, avatar }))} className={`min-h-11 rounded-lg border-2 p-2 text-2xl transition-colors focus-visible:ring-2 focus-visible:ring-teal-500 ${formData.avatar === avatar ? 'border-teal-600 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/50' : 'border-gray-200 hover:border-teal-400 dark:border-slate-600'}`}>{avatar}</button>)}</div>}
                <details className="mt-2"><summary className="cursor-pointer text-xs font-bold text-teal-800 dark:text-teal-200">Use a picture link (optional){formData.avatar_url ? ' • Added' : ''}</summary><div className="mt-2"><Input label="Picture URL" name="avatar_url" value={formData.avatar_url} onChange={handleChange} placeholder="https://…" /><p className="mt-1 text-xs text-gray-600 dark:text-slate-300">A valid http:// or https:// picture link replaces the emoji on their profile.</p></div></details>
              </div>
              <div className="grid gap-3 sm:grid-cols-2"><Input label="Create a Password" type="password" name="pin" value={formData.pin} onChange={(e) => setFormData((previous) => ({ ...previous, pin: e.target.value.slice(0, 64) }))} placeholder="6 to 64 characters" autoComplete="new-password" maxLength={64} required /><Input label="Confirm Password" type="password" name="confirm_pin" value={formData.confirm_pin} onChange={(e) => setFormData((previous) => ({ ...previous, confirm_pin: e.target.value.slice(0, 64) }))} placeholder="Re-enter the password" autoComplete="new-password" maxLength={64} required /></div>
              {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700 dark:bg-red-950/35 dark:text-red-300">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || isWriteLocked}>{loading ? 'Adding Child…' : 'Add Child'}</Button>
            </fieldset>
          </form>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AddChild;

